import { LitElement, html, css } from 'lit';
import { query, property, customElement } from 'lit/decorators.js';
import { showNotification } from '../../LanghoutBetonPortal';
import {
  unAuthenticatedAPICall,
  authenticatedAPICall,
  isSuccessfulAPIResponse,
  otpCall,
} from '../../client/api-client';
import '../../elements/langhout-button/langhout-button';
import {
  JWT_KEY_LOCALSTORAGE,
  JWT_OTP_KEY_LOCALSTORAGE,
} from '../../environment/environment';
import { sharedStyles } from '../../elements/shared-styles';
@customElement('login-element')
export class LoginForm extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        font-size: 17px;
        color: var(--text-color-main);
      }

      #buttonWrapper {
        margin-top: 30px;
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      langhout-button {
        width: 300px;
      }

      .row {
        width: 100%;
        margin-top: 30px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
      }

      .row-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }

      .label-wrapper {
        text-align: left;
        min-width: 100px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        font-weight: 900;
        padding-right: 10px;
      }

      .button-margin {
        margin-top: 140px;
      }
    `,
  ];

  @query('#otp')
  public otp?: HTMLInputElement;

  @property({ type: Number, attribute: true })
  private loginType = 0;

  @property({ type: String, attribute: true })
  private otpCode = '';

  @property({ type: String, attribute: true })
  private otpQR = '';

  constructor() {
    super();
    this.refreshOTPTokenCall();
    document.addEventListener('keypress', this.keyPressListener);
  }

  private keyPressListener = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (this.loginType === 0) {
        this.login();
      }
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keypress', this.keyPressListener);
  }

  refreshOTPTokenCall = async () => {
    const result = await authenticatedAPICall(
      '/token/refresh/otp',
      'POST',
      new Headers(),
      null,
      JWT_OTP_KEY_LOCALSTORAGE,
    );
    if (isSuccessfulAPIResponse(result)) {
      localStorage.setItem(JWT_OTP_KEY_LOCALSTORAGE, result.body.access_token);
      this.loginType = 1;
      setTimeout(() => {
        if (this.otp) {
          this.otp.focus();
        }
      }, 100);
    } else if (result.statusCode == 401) {
      localStorage.removeItem(JWT_OTP_KEY_LOCALSTORAGE);
    }
  };

  render() {
    if (this.loginType === 1) {
      document.dispatchEvent(
        new CustomEvent('hide-login', {
          detail: {},
          bubbles: true,
        }),
      );
      return html`
        ${this.renderQR()}
        <div class="row">
          <div class="label-wrapper">Verification code:</div>
          <div class="input-wrapper">
            <input
              type="number"
              id="otp"
              name="otp"
              @keypress=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                  this.verifyOTP();
                } else {
                  setTimeout(() => {
                    const verificationCode = this.otp ? this.otp.value : '';
                    if (verificationCode.length >= 6) {
                      this.verifyOTP();
                    }
                  }, 10);
                }
              }}
            />
          </div>
        </div>
        <div class="row">
          <langhout-button
            text="Verify"
            @click="${(): void => {
              this.verifyOTP();
            }}"
          ></langhout-button>
        </div>
      `;
    }
    document.dispatchEvent(
      new CustomEvent('show-login', {
        detail: {},
        bubbles: true,
      }),
    );
    return html`
      <div class="row-wrapper">
        <div class="button-margin"></div>
        <div class="row">
          <langhout-button
            text="Login"
            @click="${(): void => {
              this.login();
            }}"
          ></langhout-button>
        </div>
      </div>
    `;
  }

  renderQR = () => {
    if (this.otpQR === '') {
      return html``;
    }
    return html`
      <div class="row">Scan the QR with the Google Authenticator App.</div>
      <div class="row">
        <img src="data:image/png;base64, ${this.otpQR}" alt="OTP QR Code" />
      </div>
    `;
  };

  login = (): void => {
    const emailLogin = <HTMLInputElement>document.querySelector('#email-login');
    const email = emailLogin ? emailLogin.value : '';
    if (email === '') {
      showNotification('Please fill in your email.');
      return;
    }
    const passwordLogin = <HTMLInputElement>(
      document.querySelector('#password-login')
    );
    const password = passwordLogin ? passwordLogin.value : '';
    if (password === '') {
      showNotification('Please fill in your password.');
      return;
    }
    const basicAuth = 'Basic ' + btoa(email + ':' + password);
    this.loginCall(basicAuth);
  };

  loginCall = async (basicAuth: string) => {
    const headers = new Headers();
    headers.set('Authorization', basicAuth);
    const result = await unAuthenticatedAPICall(
      '/login/password',
      'GET',
      headers,
    );
    if (!isSuccessfulAPIResponse(result) || !result.body.access_token) {
      return;
    }
    localStorage.setItem(JWT_OTP_KEY_LOCALSTORAGE, result.body.access_token);
    if (!result.body.otp_set) {
      this.getOTP();
    }
    this.loginType = 1;
    setTimeout(() => {
      if (this.otp) {
        this.otp.focus();
      }
    }, 100);
  };

  getOTP = async () => {
    const result = await otpCall('/login/otp', 'GET', new Headers());
    if (isSuccessfulAPIResponse(result)) {
      this.otpCode = result.body.otp;
      this.otpQR = result.body.qr_code;
    }
  };

  verifyOTP = async () => {
    const verificationCode = this.otp ? this.otp.value : '';
    if (verificationCode === '') {
      showNotification('Please fill in your verification code.');
      return;
    }

    const result = await otpCall(
      '/login/otp',
      'POST',
      new Headers(),
      JSON.stringify({ code: verificationCode }),
    );
    if (this.otp) {
      this.otp.value = '';
    }
    if (isSuccessfulAPIResponse(result) && result.body.access_token) {
      localStorage.setItem(JWT_KEY_LOCALSTORAGE, result.body.access_token);
      window.location.href = '/';
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'login-element': LoginForm;
  }
}
